import React from "react"
import Waterfall from "./Waterfall"
import Donut from "./Donut"
import { fmt } from "../helpers/format"

const Stat = ({ label, value, tone }) => (
  <div className="px-4 py-3 bg-white border border-slate-200 rounded-xl">
    <div className="text-[10.5px] uppercase tracking-wider font-medium text-slate-500">{label}</div>
    <div className={`text-[18px] font-semibold tabular-nums mt-1 ${tone || "text-slate-900"}`}>{value}</div>
  </div>
)

const FinancesSection = ({ financials }) => {
  const variancePct = Number(financials.variance_pct) || 0
  const overBudget = variancePct < -5
  const surplus    = variancePct >  5

  return (
    <section id="section-finances" className="px-7 mb-12 scroll-mt-24">
      <header className="pl-[16px] mb-3">
        <h2 className="text-[15px] font-semibold text-slate-900">{I18n.t("projects.financials")}</h2>
        <p className="text-[12.5px] text-slate-500 mt-0.5">
          {I18n.t("projects.finance.subtitle")}
        </p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat label={I18n.t("projects.finance.sold")} value={fmt(financials.sold)} />
        <Stat label={I18n.t("projects.finance.planned_cost")} value={fmt(financials.planned_cost)} />
        <Stat label={I18n.t("projects.finance.actual_cost")} value={fmt(financials.actual_cost)}   tone={overBudget ? "text-rose-600" : "text-slate-900"} />
        <Stat label={I18n.t("projects.finance.margin_short")} value={fmt(financials.margin)}        tone={surplus ? "text-emerald-700" : overBudget ? "text-rose-600" : "text-slate-900"} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="text-[13px] font-semibold text-slate-800">{I18n.t("projects.finance.waterfall_title")}</h3>
            {Math.abs(variancePct) > 5 && (
              <span className={`text-[12px] font-medium ${overBudget ? "text-rose-600" : "text-emerald-700"}`}>
                {I18n.t("projects.finance.variance_pct", { value: `${variancePct > 0 ? "+" : ""}${variancePct}` })}
              </span>
            )}
          </div>
          <Waterfall steps={financials.waterfall} />
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="text-[13px] font-semibold text-slate-800 mb-3">{I18n.t("projects.finance.allocation_title")}</h3>
          <Donut items={financials.allocation} total={financials.actual_cost} />
        </div>
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_120px] gap-3 px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500 font-medium">
          <div>{I18n.t("projects.finance.table.lot")}</div>
          <div className="text-right">{I18n.t("projects.finance.table.budget")}</div>
          <div className="text-right">{I18n.t("projects.finance.actual_cost")}</div>
          <div className="text-right">{I18n.t("projects.finance.table.variance")}</div>
          <div className="text-right">{I18n.t("projects.finance.table.used")}</div>
          <div />
        </div>
        <div className="divide-y divide-slate-100">
          {financials.allocation.length === 0 ? (
            <div className="px-4 py-6 text-center text-[12.5px] text-slate-500">{I18n.t("projects.finance.no_lots")}</div>
          ) : financials.allocation.map(item => {
            const budget = item.budget?.cents || 0
            const actual = item.value?.cents || 0
            const varCents = budget - actual
            const used  = budget ? Math.round((actual / budget) * 100) : 0
            const isOver = budget && (varCents / budget) < -0.05
            const formattedVar = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(varCents / 100)
            return (
              <div key={item.id} className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_120px] gap-3 px-4 py-2.5 items-center text-[12.5px]">
                <div className="text-slate-800 font-medium truncate">{item.label}</div>
                <div className="text-right tabular-nums text-slate-700">{fmt(item.budget)}</div>
                <div className={`text-right tabular-nums ${isOver ? "text-rose-600 font-medium" : "text-slate-700"}`}>{fmt(item.value)}</div>
                <div className={`text-right tabular-nums ${isOver ? "text-rose-600 font-medium" : "text-slate-500"}`}>{formattedVar}</div>
                <div className="text-right tabular-nums text-slate-500">{used} %</div>
                <div />
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default FinancesSection
